import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { 
  MapPin, 
  Package, 
  AlertTriangle, 
  User, 
  Phone, 
  Calendar,
  ArrowLeft,
  Building2,
  Navigation
} from 'lucide-react'
import { getDonation, acceptDonation, markDonationPickedUp, markDonationDelivered, Donation } from '../api/donations'
import { isVolunteerIdApproved, volunteerIdRestrictionMessage } from '../utils/volunteerAccess'
import VolunteerIdStatusBanner from '../components/VolunteerIdStatusBanner'
import { getFoodBanks } from '../api/map'
import { useUser } from '../contexts/UserContext'
import { resolveImageUrl } from '../utils/imageUrl'

interface FoodBank {
  id: string
  name: string
  street: string
  city: string
  state: string
  latitude: number
  longitude: number
  phone?: string
  hours?: string
  distance?: number
}

const DeliveryDetailsPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useUser()
  const [donation, setDonation] = useState<Donation | null>(null)
  const [nearbyFoodBanks, setNearbyFoodBanks] = useState<FoodBank[]>([])
  const [loading, setLoading] = useState(true)
  const [claiming, setClaiming] = useState(false)
  const [confirmingPickup, setConfirmingPickup] = useState(false)
  const [confirmingDelivery, setConfirmingDelivery] = useState(false)
  const [selectedFoodBank, setSelectedFoodBank] = useState<string>('')

  const formatStatus = (status: string): string => {
    return status
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ')
  }

  useEffect(() => {
    const fetchDeliveryAndFoodBanks = async () => {
      if (!id) {
        navigate('/login')
        return
      }

      try {
        // Fetch donation details
        const donationData = await getDonation(id)
        if (!donationData) {
          alert('Donation not found')
          navigate('/')
          return
        }
        setDonation(donationData)

        // Fetch nearby food banks
        const foodBanks = await getFoodBanks()
        
        // Calculate distances and sort by proximity to donation location
        const foodBanksWithDistance = foodBanks.map((bank: any) => {
          const distance = calculateDistance(
            donationData.donationLat || 0,
            donationData.donationLng || 0,
            bank.latitude,
            bank.longitude
          )
          return {
            id: bank.id,
            name: bank.name,
            street: bank.street,
            city: bank.city,
            state: bank.state,
            latitude: bank.latitude,
            longitude: bank.longitude,
            phone: bank.phone,
            hours: bank.hours,
            distance
          }
        }).sort((a, b) => a.distance - b.distance)

        setNearbyFoodBanks(foodBanksWithDistance.slice(0, 5)) // Show top 5 closest
      } catch (error) {
        console.error('Error fetching donation details:', error)
        alert('Error loading donation details')
        navigate('/')
      } finally {
        setLoading(false)
      }
    }

    fetchDeliveryAndFoodBanks()
  }, [id, navigate])

  // Food banks should not be able to claim deliveries (only volunteers and admins can).
  // Volunteers also need approved photo ID.
  const idApproved = isVolunteerIdApproved(user)
  const idRestriction = volunteerIdRestrictionMessage(user)
  const canClaim =
    (user?.userType === 'ADMIN' || (user?.userType === 'VOLUNTEER' && idApproved)) &&
    donation?.status === 'NEW'
  
  // Check if current user is the one who claimed this donation
  const isClaimedByCurrentUser = donation?.volunteerId === user?.id
  
  // Suppliers and food banks need to see who claimed the delivery for coordination purposes
  const canSeeVolunteerInfo = user?.userType === 'SUPPLIER' || user?.userType === 'FOOD_BANK' || user?.userType === 'ADMIN'

  // Supplier-only action: confirm pickup once volunteer has claimed the donation
  const canConfirmPickup =
    user?.userType === 'SUPPLIER' &&
    donation?.supplierId === user?.id &&
    donation?.status === 'ACCEPTED' &&
    !!donation?.volunteerId

  // Food-bank action: confirm delivery after supplier confirmed pickup
  // If an assigned food bank exists, only that one should see this action.
  const canConfirmDelivery =
    user?.userType === 'FOOD_BANK' &&
    donation?.status === 'PICKED_UP' &&
    (!donation?.foodBankId || donation?.foodBankId === user?.id)

  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number) => {
    const R = 3959 // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLng = (lng2 - lng1) * Math.PI / 180
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLng/2) * Math.sin(dLng/2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
    return R * c
  }

  const handleClaimDelivery = async () => {
    if (!id || !donation) return

    if (!canClaim) {
      alert(
        user?.userType === 'VOLUNTEER' && !idApproved
          ? idRestriction
          : 'Only volunteers or admins can claim deliveries.'
      )
      return
    }

    if (!selectedFoodBank) {
      alert('Please select a food bank for donation')
      return
    }

    setClaiming(true)
    try {
      const updatedDonation = await acceptDonation(id, selectedFoodBank)
      if (updatedDonation) {
        // Update the donation state with the new data
        setDonation(updatedDonation)
      alert('Delivery claimed successfully!')
        // Optionally navigate away, or stay on page to see updated status
        // navigate('/')
      }
    } catch (error: any) {
      console.error('Error claiming donation:', error)
      alert(error?.message || 'Failed to claim donation. Please try again.')
    } finally {
      setClaiming(false)
    }
  }

  const handleConfirmPickup = async () => {
    if (!id || !donation) return
    if (!canConfirmPickup) return

    setConfirmingPickup(true)
    try {
      const updated = await markDonationPickedUp(id)
      if (updated) {
        setDonation(updated)
        alert('Pickup confirmed! The food bank can now confirm delivery once received.')
      }
    } catch (error) {
      console.error('Error confirming pickup:', error)
      alert('Failed to confirm pickup. Please try again.')
    } finally {
      setConfirmingPickup(false)
    }
  }

  const handleConfirmDelivery = async () => {
    if (!id || !donation) return
    if (!canConfirmDelivery) return

    setConfirmingDelivery(true)
    try {
      const updated = await markDonationDelivered(id)
      if (updated) {
        setDonation(updated)
        alert('Delivery confirmed! This donation is now complete.')
      }
    } catch (error) {
      console.error('Error confirming delivery:', error)
      alert('Failed to confirm delivery. Please try again.')
    } finally {
      setConfirmingDelivery(false)
    }
  }

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'URGENT':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'HIGH':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'LOW':
        return 'bg-green-100 text-green-800 border-green-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-16 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!donation) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Delivery Not Found</h1>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Back to Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/')}
            className="mr-4 p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Delivery Details</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Delivery Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Delivery Overview */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">{donation.title}</h2>
                <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border ${getUrgencyColor(donation.urgency)}`}>
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  {donation.urgency}
                </span>
              </div>
              {donation.imageUrl && (
                <div className="mb-4 overflow-hidden rounded-lg border border-gray-200 bg-neutral-100">
                  <img
                    src={resolveImageUrl(donation.imageUrl)}
                    alt={donation.title}
                    className="w-full max-h-72 object-cover"
                    loading="lazy"
                  />
                </div>
              )}
              <p className="text-gray-600 mb-6 whitespace-pre-wrap">{donation.description}</p>

              {/* Key Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-center">
                  <Package className="w-5 h-5 text-blue-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Estimated Weight</p>
                    <p className="font-medium">{donation.estimatedWeight}</p>
                  </div>
                </div>

                {donation.scheduledDate && (
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-purple-600 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Scheduled Date</p>
                      <p className="font-medium">{formatDate(donation.scheduledDate)}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center">
                  <User className="w-5 h-5 text-gray-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Contact Person</p>
                    <p className="font-medium">{donation.contactName}</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{donation.contactPhone}</p>
                  </div>
                </div>
              </div>

              {/* Special Instructions */}
              {donation.specialInstructions && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="font-medium text-yellow-800 mb-2">Special Instructions</h3>
                  <p className="text-yellow-700">{donation.specialInstructions}</p>
                </div>
              )}
            </div>

            {/* Pickup Location */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <MapPin className="w-5 h-5 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Pickup Location</h3>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="font-medium text-green-900">{donation.pickupAddress}</p>
                <div className="mt-2">
                  <p className="text-sm text-green-700">
                    From: {donation.donor?.firstName || donation.supplier?.firstName} {donation.donor?.lastName || donation.supplier?.lastName}
                  </p>
                  <p className="text-sm text-green-600 capitalize">
                    {(donation.donor?.userType || donation.supplier?.userType || 'supplier').replace('_', ' ').toLowerCase()}
                  </p>
                </div>
              </div>
            </div>

            {/* Delivery Destination */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <MapPin className="w-5 h-5 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Original Delivery Address</h3>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                {(() => {
                  const explicit = typeof (donation as any).deliveryAddress === 'string' ? String((donation as any).deliveryAddress).trim() : ''
                  const parts = [donation.deliveryStreet, donation.deliveryCity, donation.deliveryState]
                    .filter((v): v is string => typeof v === 'string' && !!v.trim() && v !== 'null')
                  const composed = parts.length > 0 ? parts.join(', ') : ''
                  const display = explicit || composed

                  if (display) {
                    return (
                      <>
                        <p className="font-medium text-red-900">{display}</p>
                <p className="text-sm text-red-600 mt-1">
                  Note: You can choose an alternative food bank from the nearby options
                </p>
                      </>
                    )
                  }

                  return (
                    <>
                      <p className="font-medium text-red-900">No original delivery address was provided.</p>
                      <p className="text-sm text-red-600 mt-1">
                        Select a food bank from the nearby options to set the delivery destination.
                      </p>
                    </>
                  )
                })()}
              </div>
            </div>
          </div>

          {/* Sidebar - Nearby Food Banks */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Building2 className="w-5 h-5 text-blue-600 mr-2" />
                Nearby Food Banks
              </h3>
              
              {nearbyFoodBanks.length === 0 ? (
                <p className="text-gray-500 text-sm">No food banks found in your area.</p>
              ) : (
                <div className="space-y-3">
                  {nearbyFoodBanks.map((bank) => (
                    <label
                      key={bank.id}
                      className={`block border rounded-lg p-3 cursor-pointer transition-colors ${
                        selectedFoodBank === bank.id
                          ? 'border-primary-600 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="foodBank"
                        value={bank.id}
                        checked={selectedFoodBank === bank.id}
                        onChange={(e) => setSelectedFoodBank(e.target.value)}
                        className="sr-only"
                      />
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 text-sm">{bank.name}</h4>
                          <p className="text-xs text-gray-600 mt-1">{bank.street}, {bank.city}, {bank.state}</p>
                          {bank.phone && (
                            <p className="text-xs text-gray-500 mt-1">{bank.phone}</p>
                          )}
                          {bank.hours && (
                            <p className="text-xs text-green-600 mt-1 font-medium">
                              📅 {bank.hours}
                            </p>
                          )}
                        </div>
                        <div className="ml-2 flex items-center text-xs text-gray-500">
                          <Navigation className="w-3 h-3 mr-1" />
                          {bank.distance?.toFixed(1)} mi
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Claim Button */}
            <div className="bg-white rounded-lg shadow p-6">
              {user?.userType === 'VOLUNTEER' && !idApproved && donation?.status === 'NEW' && (
                <div className="mb-4">
                  <VolunteerIdStatusBanner />
                </div>
              )}
              {canClaim ? (
                <>
              <button
                onClick={handleClaimDelivery}
                disabled={claiming || (nearbyFoodBanks.length > 0 && !selectedFoodBank)}
                className={`w-full btn-primary ${
                  claiming || (nearbyFoodBanks.length > 0 && !selectedFoodBank)
                    ? 'opacity-50 cursor-not-allowed' 
                    : ''
                }`}
              >
                {claiming ? 'Claiming Delivery...' : 'Claim This Delivery'}
              </button>
              
              {nearbyFoodBanks.length > 0 && !selectedFoodBank && (
                <p className="text-sm text-gray-500 mt-2 text-center">
                  Please select a food bank above
                </p>
              )}
              
              <p className="text-xs text-gray-400 mt-3 text-center">
                    By claiming this donation, you commit to picking up the items and delivering them to the selected location.
                  </p>
                </>
              ) : canConfirmPickup ? (
                <div className="text-center">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-purple-900 font-medium">This delivery has been claimed</p>
                    <p className="text-xs text-purple-700 mt-1">Next step: confirm pickup once the volunteer has collected the items.</p>
                  </div>

                  {donation.volunteer && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">Volunteer:</p>
                      <Link
                        to={`/volunteers/${donation.volunteer.id}`}
                        className="text-sm text-primary-700 font-semibold hover:underline"
                      >
                        {donation.volunteer.firstName} {donation.volunteer.lastName}
                      </Link>
                    </div>
                  )}

                  <button
                    onClick={handleConfirmPickup}
                    disabled={confirmingPickup}
                    className={`w-full btn-primary mt-4 ${confirmingPickup ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    {confirmingPickup ? 'Confirming Pickup...' : 'Confirm Pickup'}
                  </button>

                  <p className="text-xs text-gray-400 mt-3 text-center">
                    After pickup is confirmed, the assigned food bank can confirm delivery.
                  </p>
                </div>
              ) : canConfirmDelivery ? (
                <div className="text-center">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-900 font-medium">Pickup confirmed — please confirm delivery when received</p>
                    <p className="text-xs text-yellow-700 mt-1">This will complete the donation workflow.</p>
                  </div>

                  {donation.volunteer && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">Volunteer:</p>
                      <Link
                        to={`/volunteers/${donation.volunteer.id}`}
                        className="text-sm text-primary-700 font-semibold hover:underline"
                      >
                        {donation.volunteer.firstName} {donation.volunteer.lastName}
                      </Link>
                    </div>
                  )}

                  <button
                    onClick={handleConfirmDelivery}
                    disabled={confirmingDelivery}
                    className={`w-full btn-primary mt-4 ${confirmingDelivery ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    {confirmingDelivery ? 'Confirming Delivery...' : 'Confirm Delivery'}
                  </button>
                </div>
              ) : donation?.status === 'ACCEPTED' ? (
                <div className="text-center">
                  {isClaimedByCurrentUser ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm text-green-800 font-medium">✅ You have claimed this delivery</p>
                      <p className="text-xs text-green-600 mt-1">
                        Status: {formatStatus(donation.status)}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-sm text-yellow-800 font-medium">⚠️ This delivery has already been claimed</p>
                        <p className="text-xs text-yellow-600 mt-1">
                          Status: {formatStatus(donation.status)}
                        </p>
                      </div>
                      {canSeeVolunteerInfo && donation.volunteer && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs text-gray-500 mb-1">Claimed by:</p>
                          <Link
                            to={`/volunteers/${donation.volunteer.id}`}
                            className="text-sm text-primary-700 font-semibold hover:underline"
                          >
                            {donation.volunteer.firstName} {donation.volunteer.lastName}
                          </Link>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : donation?.status && donation.status !== 'NEW' ? (
                <div className="text-center">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700 font-medium">
                      Status: {formatStatus(donation.status)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      This delivery is no longer available for claiming.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-sm text-gray-700 font-medium">Only volunteers or admins can claim deliveries.</p>
                  <p className="text-xs text-gray-500 mt-2">
                    If you're a food bank, create a supply request instead — it will appear on the Food Banks page.
              </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeliveryDetailsPage